import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import drFitzer800 from "@/assets/dr-fitzer-home-900.webp.asset.json";
import drFitzer720 from "@/assets/dr-fitzer-home-720.webp.asset.json";
import drFitzer480 from "@/assets/dr-fitzer-home-480.webp.asset.json";
import { Flower } from "./Flower";
import { EditableText } from "@/components/site/EditableText";

export function AboutDoctor() {
  return (
    <section id="about" className="relative mx-auto max-w-7xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 py-3 md:py-5 grid gap-6 md:grid-cols-2 items-center">
      <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
      <div className="hidden md:flex relative justify-start">
        <div className="absolute -inset-4 rounded-2xl bg-brand-soft -z-10 w-[68%] mr-auto" />
        <img
          src={drFitzer800.url}
          srcSet={`${drFitzer480.url} 480w, ${drFitzer720.url} 720w, ${drFitzer800.url} 900w`}
          sizes="34vw"
          alt="Dr. Matthew Fitzer"
          width={800}
          height={837}
          loading="lazy"
          decoding="async"
          className="w-[68%] mr-auto rounded-xl object-cover shadow-xl"
        />
      </div>
      <div>
        <EditableText as="h2" contentKey="home.about.title" defaultValue="Meet your weight loss doctor" className="text-4xl md:text-5xl" />
        <EditableText as="p" contentKey="home.about.subtitle" defaultValue="Matthew Fitzer, M.D. — Bariatric Surgeon and Physician" className="mt-1 text-lg text-muted-foreground italic" />
        <div className="relative md:hidden -mx-6 mt-4 flex justify-center">
          <div className="absolute inset-x-0 top-4 bottom-4 rounded-2xl bg-brand-soft -z-10 w-[85%] mx-auto" />
          <img
            src={drFitzer800.url}
            srcSet={`${drFitzer480.url} 480w, ${drFitzer720.url} 720w, ${drFitzer800.url} 900w`}
            sizes="85vw"
            alt="Dr. Matthew Fitzer"
            width={800}
            height={837}
            loading="lazy"
            decoding="async"
            className="w-[85%] rounded-xl object-cover shadow-xl"
          />
        </div>
        <div className="mt-3 space-y-2 text-foreground/80 leading-[1.6]">
          <EditableText as="p" contentKey="home.about.bio-intro" defaultValue="Dr. Matthew Fitzer is a fellowship-trained bariatric surgeon who has focused on providing excellent care to overweight and obese patients since 2005. Since 2012, he has been serving patients in Herndon, Reston, Fairfax, Arlington, Alexandria, Loudoun County, and the DC metro area." multiline />
          <EditableText as="p" contentKey="home.about.bio-credentials" defaultValue="He is certified by the American Board of Surgery and is a fellow of the American College of Surgeons and the American Society of Metabolic and Bariatric Surgeons. He has founded and served as the Medical Director of MBSAQIP-Certified bariatric programs at multiple institutions. He has authored book chapters and articles that have been published in prestigious journals including the New England Journal of Medicine." multiline>
            {(value) => {
              const parts = value.split("New England Journal of Medicine");
              if (parts.length !== 2) return value;
              return (
                <>
                  {parts[0]}
                  <em>New England Journal of Medicine</em>
                  {parts[1]}
                </>
              );
            }}
          </EditableText>
          <EditableText as="p" contentKey="home.about.bio2" defaultValue="Dr. Fitzer has performed more than 6,000 laparoscopic weight-loss operations and managed weight-loss medication treatment for more than 10,000 patients in the 21+ years that he has been helping people reach their weight-loss goals." multiline>
            {(value) => {
              const opsPhrase = "more than 6,000 laparoscopic weight-loss operations";
              const medPhrase = "weight-loss medication treatment";
              const linkPhrase = (text: string, phrase: string, to: string, key: string): ReactNode => {
                const parts = text.split(phrase);
                if (parts.length !== 2) return text;
                return (
                  <>
                    {parts[0]}
                    <Link key={key} to={to} className="text-brand-deep underline hover:no-underline">
                      {phrase}
                    </Link>
                    {parts[1]}
                  </>
                );
              };
              // Link the operations phrase first, then the medication phrase inside
              // each resulting text part, so both links render.
              const opsParts = value.split(opsPhrase);
              if (opsParts.length !== 2) return value;
              return (
                <>
                  {linkPhrase(opsParts[0], medPhrase, "/medical-weight-loss/medications", "med-0")}

                  <Link to="/services" className="text-brand-deep underline hover:no-underline">
                    {opsPhrase}
                  </Link>
                  {linkPhrase(opsParts[1], medPhrase, "/medical-weight-loss/medications", "med-1")}
                </>
              );
            }}
          </EditableText>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <Link to="/about" className="rounded-full bg-brand-logo px-7 py-3 text-sm text-brand-foreground hover:opacity-90">Full Bio</Link>
          <Link to="/dr-fitzer-dissects-the-news" className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90">Recent Articles</Link>
        </div>
      </div>
    </section>
  );
}
